import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useTranslation } from "react-i18next";

export interface ToolOption {
  id: string;
  name: string;
  description?: string;
  logo_url?: string | null;
  agent_integration_id?: string; // Para uso em workflows
}

interface ToolSelectProps {
  value?: string | null;
  onValueChange: (value: string | null) => void;
  options: ToolOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  emptyValue?: string;
  className?: string;
}

export const ToolSelect: React.FC<ToolSelectProps> = ({
  value,
  onValueChange,
  options,
  placeholder,
  disabled = false,
  loading = false,
  allowEmpty = true,
  emptyLabel,
  emptyValue = "none",
  className
}) => {
  const { t } = useTranslation();
  
  const defaultPlaceholder = placeholder || t('common.selectOption');
  const defaultEmptyLabel = emptyLabel || t('common.none');
  const handleValueChange = (selectedValue: string) => {
    // Se estamos carregando e o valor selecionado não é o valor atual nem o emptyValue,
    // pode ser um reset automático do Select - ignorar
    if (loading && selectedValue !== value && selectedValue !== emptyValue && value) {
      return;
    }
    
    if (selectedValue === emptyValue) {
      onValueChange(null);
    } else {
      onValueChange(selectedValue);
    }
  };

  const selectedOption = options.find(option => option.id === value);
  
  // Se ainda estamos carregando e temos um valor, mas não encontramos nas opções,
  // preservar o valor para evitar reset automático
  const selectValue = value && !selectedOption && loading ? value : (value || emptyValue);

  // Se estamos carregando e temos um valor que não está nas opções ainda,
  // renderizar um placeholder até as opções carregarem
  if (loading && value && !selectedOption && options.length === 0) {
    return (
      <div className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background ${className}`}>
        <span className="text-muted-foreground">{t('common.loading')}</span>
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    );
  }

  return (
    <Select
      value={selectValue}
      onValueChange={handleValueChange}
      disabled={disabled || loading}
    >
      <SelectTrigger className={className}>
        <SelectValue>
          {selectedOption ? (
            <div className="flex items-center gap-2">
              {selectedOption.logo_url && (
                <img
                  src={selectedOption.logo_url}
                  alt={selectedOption.name}
                  className="w-4 h-4 object-contain flex-shrink-0"
                  onError={(e) => {
                    // Ocultar imagem se falhar ao carregar
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <span className="truncate">{selectedOption.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{defaultPlaceholder}</span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {allowEmpty && (
          <SelectItem value={emptyValue}>
            <span className="text-muted-foreground">{defaultEmptyLabel}</span>
          </SelectItem>
        )}
        
        {loading ? (
          <SelectItem value="loading" disabled>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
              <span>{t('common.loading')}</span>
            </div>
          </SelectItem>
        ) : options.length > 0 ? (
          options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              <div className="flex items-center gap-2">
                {option.logo_url && (
                  <img
                    src={option.logo_url}
                    alt={option.name}
                    className="w-4 h-4 object-contain flex-shrink-0"
                    onError={(e) => {
                      // Ocultar imagem se falhar ao carregar
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <div className="flex flex-col items-start">
                  <span className="font-medium">{option.name}</span>
                  {option.description && (
                    <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {option.description}
                    </span>
                  )}
                </div>
              </div>
            </SelectItem>
          ))
        ) : (
          <SelectItem value="no-options" disabled>
            <span className="text-muted-foreground">{t('common.noOptionsAvailable')}</span>
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
};

export default ToolSelect;
